import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "./App.css";
import { SnackbarProvider } from "./components/stateless/SnackBarProvider";
import { HimmaProvider } from "./components/HimmaProvider";
import Login from "./components/stateless/Login";
import ProtectedRoute from "./components/stateless/ProductedRoute";
import ProtectedLayout from "./components/stateless/ProductedLayout";
// import Dashboard from './components/Dashboard'; // your main page
import ManageChoiceGroup from "./components/statefull/ManageChoiceGroup/ManageChoiceGroup";
import ManageChoice from "./components/statefull/ManageChoice/ManageChoice";
import ForgotPassword from "./components/stateless/ForgotPassword";
import ResetPassword from "./components/stateless/ResetPassword";
import SetNewPassword from "./components/stateless/SetNewPassword";
import { ThemeProvider } from "@mui/material/styles";
import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { theme } from "./theme";
import i18n from './i18n';
import { createTheme } from '@mui/material/styles';
import ManageItemCategory from "./components/stateless/ManageItemCategory";
import ManageItemSubCategory from "./components/stateless/ManageItemSubCategory";
import ManageStoreItem from "./components/statefull/StoreItem/ManageStoreItem";
import NoStorePage from "./components/stateless/NoStorePage";
import OrderList from "./components/statefull/OrderManagement/OrderList";
import ErrorPage from "./components/stateless/Common/Error/ErrorPage";
import StockManagement from "./components/statefull/StockManagement/StockManagement";
import StoreManagement from "./components/statefull/StoreManagement/StoreManagement";
import ManageItemReview from "./components/statefull/ReviewManagement/ManageItemReview";
import ManageRestaurantReview from "./components/statefull/ReviewManagement/ManageRestaurantReview";
import ManageOrderReview from "./components/statefull/ReviewManagement/ManageOrderReview";
import OrderEditPage from "./components/statefull/OrderManagement/OrderEditPage";
import OrderViewPage from "./components/statefull/OrderManagement/OrderViewPage";
import { MQTTProvider } from "./Context/MQTTContext";
import Dashboard from "./components/statefull/Dashboard/Dashboard";
import CancellationPayment from "./components/statefull/CancellationPayment/CancellationPayment";
import CommissionTracking from "./components/statefull/CommissionTracking/CommissionTracking";
import CommissionTrackingHistory from "./components/statefull/CommissionTracking/CommisionTractingHistory";
import MakeyourRestaurant from "./components/statefull/MakeYourRestaurant/MakeyourRestaurant";
import Settlements from "./components/statefull/Settlements/Settlements";
import EditProfile from "./components/statefull/Profile/EditProfile";
import SalesReport from "./components/statefull/Reports/SalesReport";
import AdminCommissionReport from "./components/statefull/Reports/AdminCommissionReport";
import CancelledOrdersReport from "./components/statefull/Reports/CancelledOrdersReport";
import RejectedOrdersReport from "./components/statefull/Reports/RejectedOrdersReport";
import PendingOrdersReport from "./components/statefull/Reports/PendingOrdersReport";
// OrderSummaryReport, MerchantPayoutReport and RefundReport are not used in merchant app
// import OrderSummaryReport from "./components/statefull/Reports/OrderSummaryReport";
// import RefundReport from "./components/statefull/Reports/RefundReport";
// import MerchantPayoutReport from "./components/statefull/Reports/MerchantPayoutReport";
const Settings = lazy(() => import('./components/statefull/Profile/Settings'));
const ChangePassword = lazy(() => import('./components/statefull/Profile/ChangePassword'));
import PaymentFeaturedStripeSuccess from "./components/stateless/PaymentFeaturedStripeSuccess";
import PaymentFeaturedStripeCancel from "./components/stateless/PaymentFeaturedStripeCancel";
import HyperPayWizard from "./components/stateless/HyperPayWizard";
import SettlementHyperPayWizard from "./components/stateless/SettlementHyperPayWizard";
import PaymentFeaturedHyperPayReturn from "./components/stateless/PaymentFeaturedHyperPayReturn";
import PaymentFeaturedHyperPaySuccess from "./components/stateless/PaymentFeaturedHyperPaySuccess";
import PaymentFeaturedHyperPayCancel from "./components/stateless/PaymentFeaturedHyperPayCancel";
import SettlementHyperPayReturn from "./components/stateless/SettlementHyperPayReturn";
import SettlementHyperPaySuccess from "./components/stateless/SettlementHyperPaySuccess";
import ManageMeal from "./components/statefull/ManageMeal/ManageMeal";

const ManageStoreItemWrapper = () => {
  return <ManageStoreItem editCategory={() => { }} modal={false} />;
};
function App() {
  const [direction, setDirection] = useState<'ltr' | 'rtl'>(
    (localStorage.getItem('hm_language') || 'en') === 'ar' ? 'rtl' : 'ltr'
  );

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const dir = lng === 'ar' ? 'rtl' : 'ltr';
      setDirection(dir);
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('dir', dir);
        document.documentElement.setAttribute('lang', lng);
      }
    };
    // Initialize on mount
    handleLanguageChange(i18n.language);
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  const themed = useMemo(() => createTheme({ ...theme, direction }), [direction]);

  return (
    <ThemeProvider theme={themed}>
      <HimmaProvider baseUrl={import.meta.env.VITE_BASE_URL}>
        <SnackbarProvider>
          <Router>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/set-new-password" element={<SetNewPassword />} />
              {/* Payment callback routes (public) */}
              <Route path="/payment/featured-store/stripe/success" element={<PaymentFeaturedStripeSuccess />} />
              <Route path="/payment/featured-store/stripe/cancel" element={<PaymentFeaturedStripeCancel />} />
              <Route path="/payment/featured-store/hyperpay/wizard" element={<HyperPayWizard />} />
              <Route path="/payment/featured-store/hyperpay/return" element={<PaymentFeaturedHyperPayReturn />} />
              <Route path="/payment/featured-store/hyperpay/success" element={<PaymentFeaturedHyperPaySuccess />} />
              <Route path="/payment/featured-store/hyperpay/cancel" element={<PaymentFeaturedHyperPayCancel />} />
              {/* Settlement payment routes */}
              <Route path="/payment/settlement/hyperpay/wizard" element={<SettlementHyperPayWizard />} />
              <Route path="/payment/settlement/hyperpay/return" element={<SettlementHyperPayReturn />} />
              <Route path="/payment/settlement/hyperpay/success" element={<SettlementHyperPaySuccess />} />

              {/* Protected layout and routes */}
              <Route
                path="/layout"
                element={
                  <ProtectedRoute>
                    <MQTTProvider>
                      <ProtectedLayout />
                    </MQTTProvider>
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="manage-choice" replace />} />
                <Route path="manage-choice-group" element={<ManageChoiceGroup />} />
                <Route path="manageItemCategory" element={<ManageItemCategory />} />
                <Route path="manageItemSubCategory/:categoryId" element={<ManageItemSubCategory />} />
                <Route path="manage-choice" element={<ManageChoice />} />
                <Route path="item-management" element={<ManageStoreItemWrapper />} />
                <Route path="orderManagement" element={<OrderList />} />
                <Route path="stockManagement" element={<StockManagement />} />
                <Route path="storeManagement" element={<StoreManagement />} />
                {/* Review Management */}
                <Route path="manage-item-review" element={<ManageItemReview />} />
                <Route path="manage-restaurant-review" element={<ManageRestaurantReview />} />
                <Route path="manage-order-review" element={<ManageOrderReview />} />
                <Route path="no-store" element={<NoStorePage />} />
                <Route path="orderManagement/view/:id" element={<OrderViewPage />} />
                <Route path="orderManagement/edit/:orderId" element={<OrderEditPage />} />
                <Route path="404" element={<ErrorPage />} />
                {/* dashboard */}
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="cancellation-payment" element={<CancellationPayment />} />
                <Route path="commissionTracking" element={<CommissionTracking />} />
                <Route path="commissionTrackingHistory" element={<CommissionTrackingHistory />} />
                <Route path="makeyourRestaurant" element={<MakeyourRestaurant />} />
                <Route path="settlements" element={<Settlements />} />
                <Route path="profile" element={<EditProfile />} />
                <Route path="settings" element={<Suspense fallback={null}><Settings /></Suspense>} />
                <Route path="change-password" element={<Suspense fallback={null}><ChangePassword /></Suspense>} />
                {/* Reports */}
                <Route path="reports/sales" element={<SalesReport />} />
                {/* Order summary report route removed for merchant app */}
                {/* <Route path="reports/order-summary" element={<OrderSummaryReport />} /> */}
                <Route path="reports/admin-commission" element={<AdminCommissionReport />} />
                <Route path="reports/cancelled-orders" element={<CancelledOrdersReport />} />
                <Route path="reports/rejected-orders" element={<RejectedOrdersReport />} />
                <Route path="reports/pending-orders" element={<PendingOrdersReport />} />
                {/* Merchant payout and refund reports routes removed for merchant app */}
                {/* <Route path="reports/merchant-payout" element={<MerchantPayoutReport />} /> */}
                {/* <Route path="reports/refund" element={<RefundReport />} /> */}
                <Route path="manage-meal" element={<ManageMeal />} />

              </Route>
              <Route path="*" element={<Navigate to="/layout/404" replace />} />
            </Routes>
          </Router>
        </SnackbarProvider>
      </HimmaProvider>
    </ThemeProvider>
  );
}

export default App;
